import React, { useRef, useEffect, useState } from 'react';
import { MapPin, Navigation, ExternalLink } from 'lucide-react';

const MapComponent = ({ 
  latitude, 
  longitude, 
  title = "Location", 
  zoom = 15,
  width = "100%",
  height = "400px",
  showMarker = true,
  className = ""
}) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // Singapore bounds check
  const isValidSingaporeCoords = (lat, lng) => {
    const singLat = parseFloat(lat);
    const singLng = parseFloat(lng);
    return (
      singLat >= 1.0 && singLat <= 1.5 && 
      singLng >= 103.0 && singLng <= 104.5
    );
  };

  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);

  // Check if coordinates are valid
  const hasValidCoords = latitude && longitude && !isNaN(lat) && !isNaN(lng);
  const isInSingapore = hasValidCoords ? isValidSingaporeCoords(lat, lng) : false;

  // Load Leaflet dynamically - MUST be called before any early returns
  useEffect(() => {
    const loadLeaflet = async () => {
      if (typeof window !== 'undefined' && !window.L) {
        // Load Leaflet CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        link.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
        link.crossOrigin = '';
        document.head.appendChild(link);

        // Load Leaflet JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
        script.crossOrigin = '';
        
        return new Promise((resolve) => {
          script.onload = () => {
            setMapLoaded(true);
            resolve();
          };
          document.head.appendChild(script);
        });
      } else if (window.L) {
        setMapLoaded(true);
      }
    };

    loadLeaflet();
  }, []);

  // Initialize map - MUST be called before any early returns
  useEffect(() => {
    if (!mapLoaded || !mapContainerRef.current || mapInitialized || !hasValidCoords) return;

    // Initialize OneMap
    const map = window.L.map(mapContainerRef.current, {
      center: [lat, lng],
      zoom: zoom,
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      touchZoom: true
    });

    // Add OneMap base layer (Singapore-specific)
    window.L.tileLayer('https://www.onemap.gov.sg/maps/tiles/Default/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.onemap.gov.sg/" target="_blank">OneMap</a> | Map data © contributors, <a href="http://SLA.gov.sg">Singapore Land Authority</a>',
      maxZoom: 19,
      minZoom: 11
    }).addTo(map);

    // Add marker if enabled
    if (showMarker) {
      // Custom marker icon
      const customIcon = window.L.divIcon({
        html: `
          <div style="background: #3b82f6; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
            <div style="color: white; font-size: 12px; font-weight: bold;">📍</div>
          </div>
        `,
        className: 'custom-map-marker',
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });

      const marker = window.L.marker([lat, lng], { icon: customIcon }).addTo(map);
      
      // Add popup to marker
      marker.bindPopup(`
        <div style="min-width: 200px; font-family: system-ui;">
          <h4 style="margin: 0 0 8px 0; color: #3b82f6; font-weight: bold; font-size: 14px;">${title}</h4>
          <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">
            <div style="margin-bottom: 4px;">
              <strong>Coordinates:</strong><br/>
              ${lat.toFixed(6)}, ${lng.toFixed(6)}
            </div>
            <div style="margin-bottom: 4px;">
              <strong>Location:</strong> ${isInSingapore ? 'Singapore' : 'International'}
            </div>
          </div>
          <div style="border-top: 1px solid #e5e7eb; padding-top: 8px;">
            <a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank" style="color: #3b82f6; text-decoration: none; font-size: 11px; display: inline-flex; align-items: center;">
              🗺️ Open in Google Maps
            </a>
          </div>
        </div>
      `);
    }

    mapRef.current = map;
    setMapInitialized(true);

    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        setMapInitialized(false);
      }
    };
  }, [mapLoaded, lat, lng, zoom, showMarker, title, isInSingapore, hasValidCoords]);

  // Early return for invalid coordinates - AFTER all hooks
  if (!hasValidCoords) {
    return (
      <div className={`bg-gray-100 rounded-lg flex items-center justify-center ${className}`} style={{ height }}>
        <div className="text-center text-gray-500">
          <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No valid coordinates provided</p>
          <p className="text-xs text-gray-400">Latitude: {latitude}, Longitude: {longitude}</p>
        </div>
      </div>
    );
  }

  // Google Maps and OneMap links
  const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}&z=${zoom}`;
  const oneMapUrl = `https://www.onemap.gov.sg/?lat=${lat}&lng=${lng}&zoom=${zoom}`;

  if (!mapLoaded) {
    return (
      <div className={`bg-gray-100 rounded-lg flex items-center justify-center ${className}`} style={{ height }}>
        <div className="text-center text-gray-500">
          <div className="animate-spin w-8 h-8 mx-auto mb-2">
            <MapPin className="w-8 h-8 text-blue-500" />
          </div>
          <p className="text-sm">Loading OneMap...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative rounded-lg overflow-hidden border border-gray-200 ${className}`}>
      {/* Map Header */}
      <div className="bg-white p-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center">
          <MapPin className="w-4 h-4 mr-2 text-blue-500" />
          <span className="text-sm font-medium text-gray-900">{title}</span>
          {!isInSingapore && (
            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              Outside Singapore
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">
            {lat.toFixed(6)}, {lng.toFixed(6)}
          </span>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 p-1 rounded"
            title="Open in Google Maps"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative" style={{ height }}>
        <div 
          ref={mapContainerRef} 
          className="w-full h-full"
          style={{ minHeight: '200px' }}
        />
        
        {/* Location indicator overlay */}
        <div className="absolute bottom-2 left-2 bg-white bg-opacity-90 rounded px-2 py-1 text-xs text-gray-600 shadow-sm">
          <Navigation className="w-3 h-3 inline mr-1" />
          {isInSingapore ? 'Singapore' : 'International'} • OneMap
        </div>

        {/* Zoom controls info */}
        <div className="absolute top-2 right-2 bg-white bg-opacity-90 rounded px-2 py-1 text-xs text-gray-500 shadow-sm">
          Interactive Map
        </div>
      </div>

      {/* Map Footer with Actions */}
      <div className="bg-gray-50 p-2 text-center">
        <div className="flex justify-center space-x-4 text-xs">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 flex items-center transition-colors"
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            Google Maps
          </a>
          <a
            href={oneMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-500 hover:text-green-700 flex items-center transition-colors"
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            OneMap
          </a>
          <button
            onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                  const userLat = position.coords.latitude;
                  const userLng = position.coords.longitude;
                  const directionsUrl = `https://www.google.com/maps/dir/${userLat},${userLng}/${lat},${lng}`;
                  window.open(directionsUrl, '_blank');
                });
              } else {
                const directionsUrl = `https://www.google.com/maps/dir//${lat},${lng}`;
                window.open(directionsUrl, '_blank');
              }
            }}
            className="text-purple-500 hover:text-purple-700 flex items-center transition-colors"
          >
            <Navigation className="w-3 h-3 mr-1" />
            Directions
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapComponent; 